import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMarketStore } from '../stores/marketStore';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Check, Gift, Truck, Star, Calendar } from 'lucide-react';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  quarterlyPrice: number;
  savings: number;
  features: string[];
  popular?: boolean;
}

const subscriptionPlans: SubscriptionPlan[] = [
  {
    id: 'explorer',
    name: 'Spice Explorer',
    description: 'Perfect for home cooks who love discovering new flavors',
    monthlyPrice: 24.99,
    quarterlyPrice: 67.99,
    savings: 7,
    features: [
      'spicesMonthly',
      'recipeCards',
      'memberRecipes',
      'freeShipping',
      'discount',
    ],
  },
  {
    id: 'chef',
    name: "Chef's Collection",
    description: 'Curated selections including signature blends',
    monthlyPrice: 39.99,
    quarterlyPrice: 107.99,
    savings: 12,
    features: [
      'spicesMonthly',
      'exclusiveBlends',
      'videoTutorials',
      'recipeCards',
      'prioritySupport',
      'freeShipping',
      'discount',
    ],
    popular: true,
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'For serious cooks and small restaurants',
    monthlyPrice: 59.99,
    quarterlyPrice: 159.99,
    savings: 20,
    features: [
      'spicesMonthly',
      'bulkOptions',
      'exclusiveBlends',
      'videoCalls',
      'consultations',
      'recipeSupport',
      'prioritySupport',
      'freeShipping',
      'discount',
    ],
  },
];

export function SubscriptionSection() {
  const { t } = useTranslation('homepage');
  const formatPrice = useMarketStore(state => state.formatPrice);
  const [selectedPlan, setSelectedPlan] = useState('chef');
  const [billingCycle, setBillingCycle] = useState('monthly');

  const selectedPlanData = subscriptionPlans.find(
    plan => plan.id === selectedPlan
  );

  return (
    <section id="subscription" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t('sections.subscription.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('sections.subscription.subtitle')}
          </p>
        </div>

        {/* Benefits Overview */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <div className="text-center">
            <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Star className="h-8 w-8 text-amber-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {t('sections.subscription.benefits.curatedSelection.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('sections.subscription.benefits.curatedSelection.description')}
            </p>
          </div>
          <div className="text-center">
            <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Gift className="h-8 w-8 text-amber-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {t('sections.subscription.benefits.exclusiveBlends.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('sections.subscription.benefits.exclusiveBlends.description')}
            </p>
          </div>
          <div className="text-center">
            <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Truck className="h-8 w-8 text-amber-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {t('sections.subscription.benefits.freeDelivery.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('sections.subscription.benefits.freeDelivery.description')}
            </p>
          </div>
          <div className="text-center">
            <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-amber-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {t('sections.subscription.benefits.flexiblePlans.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('sections.subscription.benefits.flexiblePlans.description')}
            </p>
          </div>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-gray-100 p-1 rounded-lg">
            <Button
              variant={billingCycle === 'monthly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setBillingCycle('monthly')}
              className={
                billingCycle === 'monthly'
                  ? 'bg-amber-900 hover:bg-amber-800'
                  : ''
              }
            >
              {t('sections.subscription.billing.monthly')}
            </Button>
            <Button
              variant={billingCycle === 'quarterly' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setBillingCycle('quarterly')}
              className={
                billingCycle === 'quarterly'
                  ? 'bg-amber-900 hover:bg-amber-800'
                  : ''
              }
            >
              {t('sections.subscription.billing.quarterly')}
              <Badge className="ml-2 bg-green-500 text-white text-xs">
                {t('sections.subscription.billing.saveUpTo')}
              </Badge>
            </Button>
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {subscriptionPlans.map(plan => {
            const price =
              billingCycle === 'monthly'
                ? plan.monthlyPrice
                : plan.quarterlyPrice;
            const isSelected = selectedPlan === plan.id;

            return (
              <Card
                key={plan.id}
                className={`relative cursor-pointer transition-all flex flex-col h-full ${
                  isSelected
                    ? 'ring-2 ring-amber-500 shadow-lg'
                    : 'hover:shadow-lg'
                } ${plan.popular ? 'border-amber-200' : ''}`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-amber-500 text-white">
                    {t('sections.subscription.plans.chef.popular')}
                  </Badge>
                )}
                <CardHeader className="text-center">
                  <CardTitle className="text-xl text-gray-900">
                    {t(`sections.subscription.plans.${plan.id}.name`)}
                  </CardTitle>
                  <p className="text-gray-600 text-sm">
                    {t(`sections.subscription.plans.${plan.id}.description`)}
                  </p>
                  <div className="pt-4">
                    <div className="text-3xl font-bold text-gray-900">
                      {formatPrice(price)}
                      <span className="text-lg font-normal text-gray-600">
                        /
                        {billingCycle === 'monthly'
                          ? t('sections.subscription.periodLabels.mo')
                          : t('sections.subscription.periodLabels.3mo')}
                      </span>
                    </div>
                    {billingCycle === 'quarterly' && (
                      <Badge className="bg-green-100 text-green-800 mt-2">
                        {t('sections.subscription.actions.savingsPerMonth', {
                          amount: plan.savings,
                        })}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col flex-1">
                  <ul className="space-y-3 flex-1">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700">
                          {t(
                            `sections.subscription.plans.${plan.id}.features.${feature}`
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full mt-6 h-12 ${
                      isSelected
                        ? 'bg-amber-900 hover:bg-amber-800'
                        : 'bg-gray-900 hover:bg-gray-800'
                    }`}
                  >
                    {isSelected
                      ? t('sections.subscription.actions.selectedPlan')
                      : t('sections.subscription.actions.selectPlan')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Subscription Customization */}
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="text-center">
              {t('sections.subscription.customization.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label className="text-base font-medium">
                {t(
                  'sections.subscription.customization.cuisinePreferences.label'
                )}
              </Label>
              <p className="text-sm text-gray-600 mb-3">
                {t(
                  'sections.subscription.customization.cuisinePreferences.description'
                )}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  'indian',
                  'mediterranean',
                  'mexican',
                  'asian',
                  'middleEastern',
                  'americanBBQ',
                ].map(cuisine => (
                  <label
                    key={cuisine}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">
                      {t(
                        `sections.subscription.customization.cuisinePreferences.options.${cuisine}`
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-base font-medium">
                {t('sections.subscription.customization.heatPreference.label')}
              </Label>
              <RadioGroup defaultValue="medium" className="mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="mild" id="mild" />
                  <Label htmlFor="mild">
                    {t(
                      'sections.subscription.customization.heatPreference.mild'
                    )}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="medium" id="medium" />
                  <Label htmlFor="medium">
                    {t(
                      'sections.subscription.customization.heatPreference.medium'
                    )}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="hot" id="hot" />
                  <Label htmlFor="hot">
                    {t(
                      'sections.subscription.customization.heatPreference.hot'
                    )}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="bg-amber-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">
                {t('sections.subscription.customization.selectedPlan.title')}
              </h4>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">
                    {t(
                      `sections.subscription.plans.${selectedPlanData?.id}.name`
                    )}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t(
                      'sections.subscription.customization.selectedPlan.billed'
                    )}{' '}
                    {t(`sections.subscription.periodLabels.${billingCycle}`)} •{' '}
                    {t(
                      'sections.subscription.customization.selectedPlan.cancelAnytime'
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-amber-900">
                    {formatPrice(
                      billingCycle === 'monthly'
                        ? selectedPlanData?.monthlyPrice || 0
                        : selectedPlanData?.quarterlyPrice || 0
                    )}
                  </p>
                  <p className="text-sm text-gray-600">
                    {billingCycle === 'monthly'
                      ? t(
                          'sections.subscription.customization.selectedPlan.perMonth'
                        )
                      : t(
                          'sections.subscription.customization.selectedPlan.perQuarter'
                        )}
                  </p>
                </div>
              </div>
            </div>

            <Button
              size="lg"
              className="w-full bg-amber-900 hover:bg-amber-800"
            >
              {t('sections.subscription.customization.startSubscription')}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              {t('sections.subscription.customization.disclaimer')}
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
